import React, { useEffect, useState } from 'react';
import { Cookies } from 'react-cookie';
import { useRouter } from 'next/router';

import { AUTH_COOKIE_NAME, TEMP_AUTH_COOKIE_NAME, TEMP_WORKSPACE_COOKIE_NAME } from '@playbooks/utils/auth';
import { logger } from '@playbooks/utils/logger';

import * as LocalStorage from 'local-storage';

type StorageContextProps = {
	storage: any;
	getValue: any;
	storeValue: any;
	storeValues: any;
	removeValue: any;
	removeValues: any;
	getCookie: any;
	storeCookie: any;
	storeCookies: any;
	removeCookie: any;
	removeCookies: any;
	onClear: any;
	loaded: boolean;
};

type StorageProps = {
	type?: string;
	workspace?: any;
	session?: any;
	theme?: 'light' | 'dark';
	preference?: 'light' | 'dark' | 'system';
	domain?: string;
	redirect?: string;
	search?: any[];
};

const storageKey = process.env.NEXT_PUBLIC_STORAGE_KEY;

const StorageContext = React.createContext<StorageContextProps>(null);

const StorageProvider = ({ children }) => {
	const cookies = new Cookies();
	const [storage, setStorage] = useState<StorageProps>({
		type: cookies.get('type') || 'User',
		workspace: {},
		session: {},
		preference: cookies.get('preference') || 'system',
		theme: cookies.get('theme') || 'dark',
		redirect: '',
		search: [],
	});
	const [loaded, setLoaded] = useState(false);
	const router = useRouter();

	// Hooks
	useEffect(() => {
		storeValues({
			type: LocalStorage.get(`${storageKey}.type`) || storage.type,
			workspace: LocalStorage.get(`${storageKey}.workspace`) || storage.workspace,
			session: LocalStorage.get(`${storageKey}.session`) || storage.session,
			preference: LocalStorage.get(`${storageKey}.preference`) || storage.preference,
			theme: LocalStorage.get(`${storageKey}.theme`) || storage.theme,
			redirect: LocalStorage.get(`${storageKey}.redirect`) || storage.redirect,
			search: LocalStorage.get(`${storageKey}.search`) || storage.search,
		});
		setLoaded(true);
	}, []);

	useEffect(() => {
		const routes = ['/auth/login', '/auth/register', '/auth/forgot', '/auth/reset'];
		if (storage.redirect && !routes.includes(router.pathname)) removeValue(`redirect`);
	}, [router.pathname]);

	useEffect(() => {
		logger.debug('storageContext: ', storage);
	}, [storage]);

	// Methods
	const getValue = key => {
		return storage[key] ? storage[key] : LocalStorage.get(`${storageKey}.${key}`);
	};

	const storeValue = (key, value) => {
		LocalStorage.set(`${storageKey}.${key}`, value);
		setStorage({ ...storage, [key]: value });
	};

	const storeValues = data => {
		Object.keys(data).map(key => LocalStorage.set(`${storageKey}.${key}`, data[key]));
		setStorage({ ...storage, ...data });
	};

	const removeValue = key => {
		setStorage({ ...storage, [key]: null });
		LocalStorage.remove(`${storageKey}.${key}`);
	};

	const removeValues = keys => {
		return keys.map(key => removeValue(key));
	};

	// Cookies
	const getCookie = key => {
		return cookies.get(key);
	};

	const storeCookie = (key, value, wildcard = false) => {
		const versionedKey = `${key}_${process.env.NODE_ENV}`;

		return cookies.set(key, value, {
			domain: wildcard ? process.env.NEXT_PUBLIC_WILDCARD : process.env.NEXT_PUBLIC_DOMAIN,
			path: '/',
			secure: true,
			sameSite: 'lax',
		});
	};

	const storeCookies = (data, wildcard = false) => {
		return Object.keys(data).map(key => storeCookie(key, data[key], wildcard));
	};

	const removeCookie = (key, wildcard = false) => {
		const versionedKey = `${key}_${process.env.NODE_ENV}`;

		return cookies.remove(key, {
			domain: wildcard ? process.env.NEXT_PUBLIC_WILDCARD : process.env.NEXT_PUBLIC_DOMAIN,
			path: '/',
			secure: true,
		});
	};

	const removeCookies = (keys, wildcard = false) => {
		return keys.map(key => removeCookie(key, wildcard));
	};

	// Other
	const onClear = () => {
		// LocalStorage.clear();
		removeCookies(['type', 'workspace']);
		removeCookies([AUTH_COOKIE_NAME, TEMP_WORKSPACE_COOKIE_NAME, TEMP_AUTH_COOKIE_NAME], true);
		storeValues({
			type: '',
			workspace: {},
			session: {},
			preference: 'system',
			theme: 'dark',
			redirect: '',
			search: [],
		});
	};

	// Render
	return (
		<StorageContext.Provider
			value={{
				storage,
				getValue,
				getCookie,
				storeCookie,
				storeCookies,
				storeValue,
				storeValues,
				removeValue,
				removeValues,
				removeCookie,
				removeCookies,
				onClear,
				loaded,
			}}>
			{children}
		</StorageContext.Provider>
	);
};

const useStorage = () => {
	return React.useContext(StorageContext);
};

export { StorageProvider, useStorage };

// Docs:
// https://www.npmjs.com/package/local-storage
// https://www.npmjs.com/package/react-cookie
